import React, { useContext, useEffect, useState } from "react";
import { Button } from 'primereact/button';
import { apiRequest, formatFileSize, toast, url } from "@/Helpers/general";
import ConfirmDialogTemplate from '@/Components/Templates/ConfirmDialogTemplate';
import { getFormErrorMessage, isFormFieldValid, validateForm } from "@/Helpers/forms";
import { AppContext } from "@/Components/Context/AppContext";
import { createStorageObject } from "@/Helpers/storage";
import FileUploadTemplate from "@/Components/Templates/Forms/FileUploadTemplate";
import { CLAIM_DOCUMENTS_ALLOWED_EXTENSIONS } from "@/Config/constants";
import { Img } from "react-image";

/**
 * Component which handles the "automated parsing (OCR)" tab of creating a new claim
 */
const NewClaimOCRForm = () => {
    const { toastRef } = useContext(AppContext);

    const [formLoading, setFormLoading] = useState(false);
    const [uploadDisabled, setUploadDisabled] = useState(false);
    const [mainFile, setMainFile] = useState();
    const [attachments, setAttachments] = useState([]);
    const [showMandatoryFileMsg, setShowMandatoryFileMsg] = useState(false);
    const [showDuplicateDialogConfirm, setDuplicateDialogConfirm] = useState(false);

    /**
     * Submits the new claim form
     */
    const submitForm = async (force) => {
        if (mainFile) {
            setShowMandatoryFileMsg(false);
            setFormLoading(true);

            let formData = new FormData();

            if(force){
               formData.append('force', true);
            }

            formData.append('mainFile', mainFile);

            if (attachments) {
                attachments.forEach((file) => {
                    formData.append('attachments[]', file);
                });
            }

            apiRequest('post', 'request/ocr', {
                data: formData,
                onSuccess: (response) => {
                    // console.log(response);
                    setFormLoading(false);

                    if (response?.data?.responseType == 'success') {
                        createStorageObject('flashMessages', [{
                            type: 'success',
                            title: 'Success',
                            message: 'The claim has been created.'
                        }]);

                        window.location.replace(url('home'));

                    } else {
                        toast(toastRef, 'error', 'Error creating claim.');
                    }
                },
                onFailure: (error) => {
                    setFormLoading(false);
                    if (error.status == 403) {
                        if (error.messages == 'This file was already uploaded!') {
                        setDuplicateDialogConfirm(true);
                        }
                    }
                }
            });

        } else {
            setShowMandatoryFileMsg(true);
        }
    };

    /**
     * Add files in the state
     *
     * @param {object} event data related to the files
     */
    const uploadHandler = (event, type) => {
        switch (type) {
            case 'mainFile':
                setMainFile(event.files[0]);
                setShowMandatoryFileMsg(false);
                setUploadDisabled(true);
                break;

            case 'attachments':
                setAttachments(event.files);
                break;

            default:
                break;
        }
    };

    /**
     * Remove a file from the state
     *
     * @param {object} event data related to the file
     */
    const onFileRemove = (event, type) => {

        switch (type) {
            case 'mainFile':
                setMainFile();
                setUploadDisabled(false);
                break;

            case 'attachments':
                let copyAttachments = [...attachments];
                if (event?.file?.objectURL) {
                    copyAttachments = copyAttachments.filter((file) => file.objectURL != event.file.objectURL);
                }
                setAttachments(copyAttachments);
                break;

            default:
                break;
        }

    };

     /**
     * Generate a preview of a file
     *
     * @param {object} doc data related to the file
     */
     const filePreview = (doc, uploadPreview = false) => {
		let preview = "";
		let type = uploadPreview ? doc?.type : doc?.file_mime;

		switch (type) {
			case "application/pdf":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-pdf"></i>
					</div>
				);
				break;
			case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-word"></i>
					</div>
				);
				break;
			case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-excel"></i>
					</div>
				);
				break;
			case "application/msword":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-word"></i>
					</div>
				);
				break;
			case "application/vnd.ms-excel":
				preview = (
					<div className="no-picture">
						<i className="pi pi-file-excel"></i>
					</div>
				);
				break;
			default:
				preview = (
					<div className="no-picture">
						<i className="pi pi-file"></i>
					</div>
				);
				break;
		}

		if (type.search("image") !== -1) {
			let getImg = uploadPreview ? doc.objectURL : imgUrls.find((img) => img.id == doc.id);

			if (getImg) {
				preview = (
					<Img
						src={getImg.url}
						alt=""
						unloader={
							<div className="no-picture">
								<i className="pi pi-image"></i>
							</div>
						}
					/>
				);
			}
		}

		return preview;
	};

    /**
     * The preview (for files other than images) which appears after you select a file
     */
    const itemTemplate = (file, event) => {
        return (
            <div className="p-fileupload-row other-files-format">
                <div className="file-extension-icon">{filePreview(file, true)}</div>

                <div className="p-fileupload-filename">{file.name}</div>

                <div>{formatFileSize(file.size)}</div>

                <div>
                    <button type="button" className="p-button p-component p-button-icon-only" onClick={() => {
                        event.onRemove();
                        setMainFile();
                    }}>
                        <span className="p-button-icon p-c pi pi-times"></span>
                        <span className="p-button-label p-c">&nbsp;</span>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="general-form">
                <div className="main-content grid">
                    <div className="col-6">
                        <label htmlFor="" className="block mb-3">PDF file for parsing:</label>
                        <FileUploadTemplate uploadHandler={(e) => uploadHandler(e, 'mainFile')}
                            disabled={uploadDisabled}
                            multiple={false}
                            onFileRemove={(e) => onFileRemove(e, 'mainFile')}
                            accept=".pdf"
                            itemTemplate={itemTemplate}
                        />
                        {showMandatoryFileMsg && <small className={'p-error block mt-1 w-full'}>PDF file is mandatory.</small>}
                    </div>

                    <div className="col-6">
                        <label htmlFor="" className="block mb-3">Additional attachment files:</label>
                        <FileUploadTemplate uploadHandler={(e) => uploadHandler(e, 'attachments')}
                            onFileRemove={(e) => onFileRemove(e, 'attachments')}
                            accept={CLAIM_DOCUMENTS_ALLOWED_EXTENSIONS.join(',')}
                            itemTemplate={itemTemplate}
                        />
                    </div>
                </div>

                <hr />
                <div className="text-right">
                    <Button label="Save" onClick={() => submitForm(false)} loading={formLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center" />
                </div>
            </div>
            <ConfirmDialogTemplate
                title="Duplicate file"
                message={'The file you selected has already been uploaded. Do you want to continue processing this document even if it is a duplicate?'}
                onConfirm={() => {}}
                visible={showDuplicateDialogConfirm}
                setVisible={setDuplicateDialogConfirm}
                footer={
                    <>
                        <Button
                            label="Continue processing"
                            className="p-button-outlined mr-3"
                            onClick={() => {
                                submitForm(true);
                                setDuplicateDialogConfirm(false);
                            }}
                            loading={formLoading}
                            loadingIcon="pi pi-spin pi-sun"
                            iconPos="center"
                        />
                        <Button
                            label="Cancel"
                            className="p-button-outlined p-button-secondary"
                            onClick={() => {
                                setDuplicateDialogConfirm(false);
                            }}
                        />
                    </>
                }
            />

        </>
    );
};

export default NewClaimOCRForm;
